import React, { useMemo } from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { buildColorMap, orderTypes, typeKey, typeLabel } from '../../utils/categoricalColors';
import {
  formatDay,
  formatDayNumber,
  formatDayShort,
  formatInt,
  formatMonth,
  formatMonthShort,
} from '../../utils/numberFormat';
import { granularityLabel } from '../../utils/usagePeriod';

const etichettaBucket = (bucket, granularity) => {
  const g = String(granularity).toLowerCase();
  if (g === 'month') return formatMonth(bucket.bucketStart);
  if (g === 'week') return `${formatDayShort(bucket.bucketStart)} – ${formatDayShort(bucket.bucketEnd)}`;
  return formatDay(bucket.bucketStart);
};

/**
 * L'asse porta l'etichetta corta, il tooltip quella estesa: Recharts scarta i tick che violano
 * `minTickGap`, e con le etichette lunghe i bucket sparivano dall'asse uno sì e uno no — senza che
 * mancasse il dato, che resta nella barra e nel tooltip.
 *
 * Sul giornaliero l'etichetta è il solo numero del giorno, **sempre**, primo tick incluso. Le
 * etichette larghe uguali sono il punto: mescolarne una lunga ("20 ago") fra le corte non fa
 * scartare quella, fa scartare le vicine, e lo scarto cade dove capita — nella prima versione si
 * era portato via il 21 e il marcatore "1 set", cioè proprio il cambio mese.
 * Il mese lo danno l'intervallo stampato sopra il grafico e il tooltip, che resta esteso.
 */
export const etichettaAsse = (bucket, granularity) => {
  const g = String(granularity).toLowerCase();
  if (g === 'month') return formatMonthShort(bucket.bucketStart);
  if (g === 'week') return formatDayShort(bucket.bucketStart);
  return formatDayNumber(bucket.bucketStart);
};

/** Tooltip con il dettaglio per tipo e il totale del bucket: i tipi a zero non si stampano. */
const ChartTooltip = ({ active, payload, colorByType }) => {
  if (!active || !payload?.length) return null;
  const bucket = payload[0].payload;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200 }}>
      <Typography variant="subtitle2" gutterBottom>
        {bucket.label}
      </Typography>
      {bucket.tipi
        .filter((tipo) => tipo.pages > 0)
        .map((tipo) => (
          <Stack key={tipo.key} direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colorByType.get(tipo.key) }} />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {tipo.label}
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatInt(tipo.pages)}
            </Typography>
          </Stack>
        ))}
      <Stack direction="row" spacing={1} sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Totale
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {formatInt(bucket.total)}
        </Typography>
      </Stack>
    </Paper>
  );
};

/**
 * Barre impilate per tipo di documento.
 *
 * Le dataKey delle serie sono gli id dei tipi, non le etichette: una rinomina del tipo non cambia
 * colore e non rompe lo stack. L'ordine dei <Bar> è l'ordine categoriale fisso, quindi cambiare
 * periodo non rimescola i colori. Un solo stackId: niente secondo asse.
 */
const UsagePagesChart = ({ timeseries, granularity }) => {
  const theme = useTheme();

  const { tipi, colorByType, data } = useMemo(() => {
    const buckets = timeseries?.buckets ?? [];
    const tipiPresenti = new Map();
    buckets.forEach((bucket) => {
      (bucket.byCodexType ?? []).forEach((tipo) => {
        const key = typeKey(tipo.codexTypeId);
        if (!tipiPresenti.has(key)) {
          tipiPresenti.set(key, { key, label: typeLabel(tipo), codexTypeCode: tipo.codexTypeCode });
        }
      });
    });

    const ordinati = orderTypes([...tipiPresenti.values()]);
    const colori = buildColorMap(ordinati.map((tipo) => tipo.key));

    const righe = buckets.map((bucket) => {
      const perTipo = (bucket.byCodexType ?? []).reduce((acc, tipo) => {
        acc[typeKey(tipo.codexTypeId)] = tipo.pages;
        return acc;
      }, {});

      return {
        label: etichettaBucket(bucket, granularity),
        axisLabel: etichettaAsse(bucket, granularity),
        total: bucket.total,
        tipi: ordinati.map((tipo) => ({ ...tipo, pages: perTipo[tipo.key] ?? 0 })),
        ...ordinati.reduce((acc, tipo) => ({ ...acc, [tipo.key]: perTipo[tipo.key] ?? 0 }), {}),
      };
    });

    return { tipi: ordinati, colorByType: colori, data: righe };
  }, [timeseries, granularity]);

  if (!data.length) {
    return (
      <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Nessuna pagina elaborata nel periodo selezionato</Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {tipi.map((tipo) => (
          <Stack key={tipo.key} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colorByType.get(tipo.key) }} />
            <Typography variant="body2" color="text.secondary">
              {tipo.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ height: 300 }} aria-label={`Pagine elaborate ${granularityLabel(granularity)}, per tipo di documento`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={theme.palette.divider} />
            <XAxis dataKey="axisLabel" interval="preserveStartEnd" minTickGap={12} tickLine={false} />
            <YAxis tickFormatter={formatInt} width={64} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              content={<ChartTooltip colorByType={colorByType} />}
            />
            {tipi.map((tipo) => (
              <Bar
                key={tipo.key}
                dataKey={tipo.key}
                stackId="pagine"
                fill={colorByType.get(tipo.key)}
                isAnimationActive={false}
                maxBarSize={34}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

export default UsagePagesChart;
